import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Box, Skeleton } from '@mui/material';

const DashboardWidgetVisibleContext = createContext(true);

export function useDashboardWidgetVisible(): boolean {
  return useContext(DashboardWidgetVisibleContext);
}

interface DashboardWidgetViewportProps {
  children: React.ReactNode;
}

/**
 * Keeps below-the-fold widgets from joining the dashboard's cold-start burst.
 * Once a card approaches the viewport it stays mounted, preserving its local
 * state while scrolling. The context continues to track visibility so live
 * widgets can pause polling when the card is off screen.
 */
const DashboardWidgetViewport: React.FC<DashboardWidgetViewportProps> = ({ children }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const observerSupported = typeof IntersectionObserver !== 'undefined';
  const [activated, setActivated] = useState(!observerSupported);
  const [visible, setVisible] = useState(!observerSupported);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setActivated(true);
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const nextVisible = entry.isIntersecting;
        setVisible(nextVisible);
        if (nextVisible) setActivated(true);
      },
      { rootMargin: '64px 0px', threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <DashboardWidgetVisibleContext.Provider value={visible}>
      <Box ref={ref} sx={{ height: '100%', minHeight: 0 }}>
        {activated ? children : (
          <Box aria-hidden="true" sx={{ height: '100%', py: 1 }}>
            <Skeleton variant="rounded" height="100%" animation="wave" />
          </Box>
        )}
      </Box>
    </DashboardWidgetVisibleContext.Provider>
  );
};

export default DashboardWidgetViewport;
